import { Injectable } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import { Observable, from } from "rxjs";
import { filter, switchMap } from "rxjs/operators";
import { GuideStep } from "./guide-step.model";
import { GuideService } from "./guide.service";

/**
 * Espera o seletor aparecer no DOM (útil em telas lazy-loaded).
 * Emite o elemento assim que existir; erra ao esgotar o timeout.
 */
export function waitForElement(
  selector: string,
  timeoutMs = 3000
): Observable<Element> {
  return new Observable<Element>((subscriber) => {
    const existing = document.querySelector(selector);
    if (existing) {
      subscriber.next(existing);
      subscriber.complete();
      return undefined;
    }

    let finished = false;
    const finish = () => {
      if (finished) {
        return;
      }
      finished = true;
      clearTimeout(timer);
      observer.disconnect();
    };

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (!el) {
        return;
      }
      finish();
      subscriber.next(el);
      subscriber.complete();
    });

    const timer = setTimeout(() => {
      finish();
      subscriber.error(
        new Error(
          `[GuideNavigator] Timeout de ${timeoutMs}ms aguardando "${selector}".`
        )
      );
    }, timeoutMs);

    observer.observe(document.body, { childList: true, subtree: true });

    return () => finish();
  });
}

/**
 * Companheiro do GuideService: navega entre rotas do passo, espera o alvo no DOM e
 * encerra o guia se o usuário navegar manualmente (evita overlay órfão).
 * O motor permanece sem conhecer Router.
 */
@Injectable({
  providedIn: "root",
})
export class GuideNavigatorService {
  private isGuideNavigating = false;
  private listening = false;

  constructor(
    private readonly router: Router,
    private readonly guideService: GuideService
  ) {}

  listen(): void {
    if (this.listening) {
      return;
    }
    this.listening = true;

    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.guideService.isActive && !this.isGuideNavigating) {
          this.guideService.finish("skipped");
        }
      });
  }

  waitForElement(selector: string, timeoutMs = 3000): Observable<Element> {
    return waitForElement(selector, timeoutMs);
  }

  /**
   * Garante a rota do passo (se houver) e só então espera o elemento-alvo.
   */
  prepareTarget(step: GuideStep, timeoutMs = 3000): Observable<Element> {
    return from(this.ensureRoute(step)).pipe(
      switchMap(() => this.waitForElement(step.targetSelector, timeoutMs))
    );
  }

  private async ensureRoute(step: GuideStep): Promise<void> {
    if (!step.route) {
      return;
    }

    const current = this.currentPath();
    if (this.routeMatches(current, step.route)) {
      return;
    }

    if (step.route.includes(":")) {
      return;
    }

    this.isGuideNavigating = true;
    try {
      await this.router.navigateByUrl(step.route);
    } finally {
      this.isGuideNavigating = false;
    }
  }

  private currentPath(): string {
    return this.router.url.split("?")[0];
  }

  private routeMatches(currentUrl: string, stepRoute: string): boolean {
    if (!stepRoute.includes(":")) {
      return currentUrl === stepRoute;
    }
    const pattern = `^${stepRoute.replace(/:[^/]+/g, "[^/]+")}$`;
    return new RegExp(pattern).test(currentUrl);
  }
}
